import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import http from "../../../commonFunctions/httpClient";
import config from "../../../Config/config.json";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";

export interface LabTechnicianOption {
  technicianId: string;
  name: string;
  status: string;
  contact?: string;
  experience?: number | string;
  profileImage?: string;
}

interface AssignTechnicianDialogProps {
  open: boolean;
  labId: string;
  bookingId: string;
  patientName?: string;
  // Shown in the subtitle; bookingId is only used for the API call
  orderNumber?: string;
  onClose: () => void;
  onAssigned: (technician: LabTechnicianOption) => void;
}

const COLORS = {
  primary: "#098963",
  dark: "#022219",
  muted: "#828584",
  border: "#CEEEE5",
  light: "#E7F6F2",
};

// Profile images are stored as server-relative paths
const imageOrigin = (() => {
  try {
    return new URL(config.apiBaseUrl).origin;
  } catch {
    return "";
  }
})();

// Every technician is listed; free ones first, then busy ("Assigned" / "On Route"), then offline
const statusRank = (tech: LabTechnicianOption) => {
  switch ((tech.status || "").trim().toLowerCase()) {
    case "active":
      return 0;
    case "assigned":
    case "on route":
      return 1;
    default:
      return 2;
  }
};

const getInitials = (name: string) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

export const AssignTechnicianDialog: React.FC<AssignTechnicianDialogProps> = ({
  open,
  labId,
  bookingId,
  patientName,
  orderNumber,
  onClose,
  onAssigned,
}) => {
  const { showMessage } = useSnackbar();
  const [technicians, setTechnicians] = useState<LabTechnicianOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (!open || !labId) return;
    setSearch("");
    setSelectedId("");

    const fetchTechnicians = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const response: any = await http.get(`lab/Technician/${labId}/Technician`);
        const data: any[] = Array.isArray(response?.data) ? response.data : [];
        setTechnicians(
          data.map((tech) => ({
            technicianId: tech.technicianId,
            name: tech.name || "Unnamed",
            status: tech.status || "Offline",
            contact: tech.contact,
            experience: tech.experience,
            profileImage: tech.profileImage ? `${imageOrigin}${tech.profileImage}` : undefined,
          }))
        );
      } catch (e) {
        console.error("Failed to load technicians:", e);
        setLoadError(true);
        setTechnicians([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTechnicians();
  }, [open, labId]);

  const visibleTechnicians = useMemo(() => {
    const term = search.trim().toLowerCase();
    return technicians
      .filter((tech) => !term || `${tech.name} ${tech.contact || ""}`.toLowerCase().includes(term))
      .sort((a, b) => statusRank(a) - statusRank(b));
  }, [technicians, search]);

  const handleAssign = async () => {
    const technician = technicians.find((t) => t.technicianId === selectedId);
    if (!technician) return;

    setAssigning(true);
    try {
      const response: any = await http.post("lab/CollectionDetailes", {
        technicianId: technician.technicianId,
        labBookingId: bookingId,
      });
      if (response?.success === false) {
        throw new Error(response?.message || "Failed to assign technician");
      }
      showMessage(`Order assigned to ${technician.name}`, "success");
      onAssigned(technician);
    } catch (error: any) {
      console.error("Assign technician error:", error);
      showMessage(
        error?.response?.data?.message || error?.message || "Failed to assign technician",
        "error"
      );
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={assigning ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "16px" } }}
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Typography sx={{ fontWeight: 700, color: COLORS.dark }}>Assign technician</Typography>
        <Typography sx={{ color: COLORS.muted, fontSize: "0.8rem" }} noWrap>
          {[orderNumber ? `Order #${orderNumber}` : "", patientName].filter(Boolean).join(" · ")}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={assigning}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: "4px !important" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search technician"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: COLORS.muted }} />
              </InputAdornment>
            ),
            sx: { borderRadius: "10px" },
          }}
          sx={{ mb: 1.5 }}
        />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} sx={{ color: COLORS.primary }} />
          </Box>
        ) : loadError ? (
          <Typography sx={{ color: "#D32F2F", textAlign: "center", py: 3 }}>
            Failed to load technicians.
          </Typography>
        ) : visibleTechnicians.length === 0 ? (
          <Typography sx={{ color: COLORS.muted, textAlign: "center", py: 3 }}>
            {technicians.length === 0 ? "No technicians added yet." : "No technicians match your search."}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 360, overflowY: "auto" }}>
            {visibleTechnicians.map((tech) => {
              const selected = tech.technicianId === selectedId;
              return (
                <Box
                  key={tech.technicianId}
                  role="radio"
                  aria-checked={selected}
                  tabIndex={0}
                  onClick={() => setSelectedId(tech.technicianId)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedId(tech.technicianId);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.25,
                    borderRadius: "12px",
                    cursor: "pointer",
                    border: `1px solid ${selected ? COLORS.primary : COLORS.border}`,
                    bgcolor: selected ? COLORS.light : "#fff",
                    outline: "none",
                    "&:hover, &:focus-visible": { borderColor: COLORS.primary },
                  }}
                >
                  <Avatar src={tech.profileImage} sx={{ width: 40, height: 40, bgcolor: COLORS.primary, fontSize: "0.9rem" }}>
                    {getInitials(tech.name)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, color: COLORS.dark, fontSize: "0.9rem" }} noWrap>
                      {tech.name}
                    </Typography>
                    <Typography sx={{ color: COLORS.muted, fontSize: "0.75rem" }} noWrap>
                      {[tech.contact, tech.experience ? `${tech.experience} yrs exp` : ""].filter(Boolean).join(" · ") || "-"}
                    </Typography>
                  </Box>
                  {selected && <CheckCircleIcon sx={{ color: COLORS.primary, fontSize: 20 }} />}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={assigning} sx={{ color: COLORS.muted, textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleAssign}
          disabled={!selectedId || assigning}
          startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            bgcolor: COLORS.primary,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { bgcolor: "#077352" },
          }}
        >
          {assigning ? "Assigning..." : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignTechnicianDialog;
